// Image filename parser — speglar backend parse_image_filename i server.py.
//
// Strategi: Vårdväskans PartNo har formatet `\d{4,}(-\d{4,})+` (t.ex.
// `10001748-1077-10047`). Vi förankrar parsern i detta:
//   1. Extrahera PartNo-prefixet (segmenten med minst 4 siffror).
//   2. Allt EFTER PartNo betraktas som suffix-kandidat.
//   3. Suffix-mönster: `_ (N)`, `(N)`, `_N`, `-N`, `.N` → N=1 ⇒ main, annars additional.
//   4. Tom rest ⇒ main, idx 1.
//   5. Om inget PartNo-prefix → matched=false (gå via filename_mappings).
//
// Exempel:
//   '10001748-1077-10047.jpg'       → PartNo '10001748-1077-10047', main
//   '10001748-1077-10047_ (2).jpg'  → PartNo '10001748-1077-10047', additional
//   '10001748-1077-10047_2.jpg'     → additional
//   '10001748-1077-10047-2.jpg'     → additional
//   '10001748-1077-10047 (1).jpg'   → main
//   'BEEZ_jacket_red.jpg'           → matched=false (mappnings-fallback)
const PARTNO_RE = /^\d{4,}(?:-\d{4,})+/;

export function parseImageFilename(filename) {
  const name = filename.replace(/\.[^.]+$/, '');

  const partNoMatch = name.match(PARTNO_RE);
  if (!partNoMatch) {
    return { partNo: name, imageType: 'main', index: 1, matched: false };
  }
  const partNo = partNoMatch[0];
  const rest = name.slice(partNo.length);

  if (rest === '') {
    return { partNo, imageType: 'main', index: 1, matched: true };
  }

  // Parens-format: '_ (2)', ' (2)', '_(2)', '(2)'
  const paren = rest.match(/^[_ ]?\s*\((\d+)\)$/);
  if (paren) {
    const idx = parseInt(paren[1], 10);
    return { partNo, imageType: idx === 1 ? 'main' : 'additional', index: idx, matched: true };
  }

  // Enkelt suffix: '_2', '-2', '.2'
  const suffix = rest.match(/^[.\-_](\d+)$/);
  if (suffix) {
    const idx = parseInt(suffix[1], 10);
    return { partNo, imageType: idx === 1 ? 'main' : 'additional', index: idx, matched: true };
  }

  // PartNo matchade men efterledet är okänt — låt mappnings-fallback ta över.
  return { partNo: name, imageType: 'main', index: 1, matched: false };
}

// Försök matcha filename mot en lista mappningar (i prioritetsordning exact → prefix
// → suffix → contains → regex). Returnerar { mapping, partNo, imageType } eller null.
export function applyMappings(filename, mappings) {
  if (!mappings || mappings.length === 0) return null;
  const active = mappings.filter(m => m.is_active);
  const byType = type => active.filter(m => m.pattern_type === type);
  const order = ['exact', 'prefix', 'suffix', 'contains', 'regex'];
  for (const type of order) {
    for (const m of byType(type)) {
      let hit = false;
      try {
        if (type === 'exact')    hit = filename === m.filename_pattern;
        if (type === 'prefix')   hit = filename.startsWith(m.filename_pattern);
        if (type === 'suffix')   hit = filename.endsWith(m.filename_pattern);
        if (type === 'contains') hit = filename.includes(m.filename_pattern);
        if (type === 'regex')    hit = new RegExp(m.filename_pattern).test(filename);
      } catch { /* ogiltig regex — hoppa */ }
      if (hit) return { mapping: m, partNo: m.part_no, imageType: m.image_type };
    }
  }
  return null;
}

// Gruppera File-objekt på partNo. Kör standardparsern → fallback till mappningar.
// Filer utan match hamnar i { unknown: [files] }.
// Returns { groups: { partNo: { main, additional, _files } }, unknown: [files], matchedMappingIds: [] }
export function groupFilesByProduct(files, mappings = []) {
  const groups = {};
  const unknown = [];
  const matchedMappingIds = new Set();

  files.forEach(file => {
    let parsed = parseImageFilename(file.name);
    if (!parsed.matched) {
      const mres = applyMappings(file.name, mappings);
      if (mres) {
        parsed = { partNo: mres.partNo, imageType: mres.imageType, index: 1, matched: true };
        matchedMappingIds.add(mres.mapping.id);
      } else {
        unknown.push(file);
        return;
      }
    }
    const { partNo, imageType, index } = parsed;
    if (!groups[partNo]) groups[partNo] = { main: null, additional: [], _files: [] };
    groups[partNo]._files.push({ file, imageType, index });
    if (imageType === 'main' && !groups[partNo].main) {
      groups[partNo].main = file;
    } else {
      groups[partNo].additional.push({ file, index });
    }
  });

  Object.values(groups).forEach(g => {
    g.additional.sort((a, b) => a.index - b.index);
    g.additional = g.additional.map(x => x.file);
  });

  return { groups, unknown, matchedMappingIds: Array.from(matchedMappingIds) };
}
